import { SWRResponse } from "swr";
import { ApiSchemaActivity, ApiSchemaActivityLogCompany } from "../../models/api";
import { AxiosAuth } from "../axios";
import {
  useExtendedApiAccess,
  CreateAccessObject,
} from "./useExtendedApiAccess";
import { AppRouteApi } from "../../config/appRoute";
import { PaginatedData } from "../../models/api";
import { ActionsCaregiverActivityTable, useAppSelector } from "../redux";
import { useRef, useEffect } from "react";
import { ApiResponseContainer } from "../../models/api";
import { useAppDispatch } from "../redux/ReduxStore";
import { StorageAuth } from "../localStorage";
import { ActionsPatientActivityTable } from "../redux/slices/slicePatientActivityTable";

const CacheIds = {
  Patient: AppRouteApi.AppointmentActivity.Patient(),
  Caregiver: AppRouteApi.AppointmentActivity.Caregiver(),
  Company: AppRouteApi.AppointmentActivity.Company(),
};

function useApiPatientActivity(
  patientId: string,
  companyId?: string
): SWRResponse<ApiSchemaActivity[] | undefined, Error> {
  const dispatch = useAppDispatch();
  const isMounted = useRef(false);

  const api = useExtendedApiAccess<ApiSchemaActivity[]>(CacheIds.Patient, () =>
    AxiosAuth.get(CacheIds.Patient, {
      params: {
        CompanyId: companyId ? companyId : StorageAuth.CompanyId,
        PatientId: patientId,
      },
    })
  );

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
    } else {
      (async () => {
        dispatch(ActionsPatientActivityTable.setIsLoading(true));
        const res = await AxiosAuth.get<
          ApiResponseContainer<ApiSchemaActivity[]>
        >(CacheIds.Patient, {
          params: {
            CompanyId: companyId ? companyId : StorageAuth.CompanyId,
            PatientId: patientId,
          },
        });

        if (res.data.isSuccess) {
          api.mutate(res.data.data, false);
        }
        dispatch(ActionsPatientActivityTable.setIsLoading(false));
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, patientId]);

  return api;
}

function useApiCaregiverActivity(
  caregiverId: string,
  companyId?: string
): SWRResponse<PaginatedData<ApiSchemaActivity> | undefined, Error> {
  const { pageNumber, pageSize, searchKey } = useAppSelector(
    (state) => state.caregiverActivityTable
  );
  const dispatch = useAppDispatch();
  const isMounted = useRef(false);

  const api = useExtendedApiAccess<PaginatedData<ApiSchemaActivity>>(
    CacheIds.Caregiver,
    () =>
      AxiosAuth.get(CacheIds.Caregiver, {
        params: {
          PageNumber: pageNumber,
          PageSize: pageSize,
          SearchKey: searchKey,
          CompanyId: companyId ? companyId : StorageAuth.CompanyId,
          CaregiverId: caregiverId,
        },
      })
  );

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
    } else {
      (async () => {
        dispatch(ActionsCaregiverActivityTable.setIsLoading(true));
        const res = await AxiosAuth.get<
          ApiResponseContainer<PaginatedData<ApiSchemaActivity>>
        >(CacheIds.Caregiver, {
          params: {
            PageNumber: pageNumber,
            PageSize: pageSize,
            SearchKey: searchKey,
            CompanyId: companyId ? companyId : StorageAuth.CompanyId,
            CaregiverId: caregiverId,
          },
        });

        if (res.data.isSuccess) {
          api.mutate(res.data.data, false);
        }
        dispatch(ActionsCaregiverActivityTable.setIsLoading(false));
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, pageSize, searchKey, companyId, caregiverId]);

  return api;
}

function useApiCompanyActivity(
  companyId: string
): SWRResponse<PaginatedData<ApiSchemaActivityLogCompany> | undefined, Error> {
  const { pageNumber, pageSize, searchKey } = useAppSelector(
    (state) => state.caregiverActivityTable
  );
  const dispatch = useAppDispatch();
  const isMounted = useRef(false);

  const api = useExtendedApiAccess<PaginatedData<ApiSchemaActivityLogCompany>>(
    CacheIds.Company,
    () =>
      AxiosAuth.get(CacheIds.Company, {
        params: {
          PageNumber: pageNumber,
          PageSize: pageSize,
          SearchKey: searchKey,
          CompanyId: companyId,
        },
      })
  );

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
    } else {
      (async () => {
        dispatch(ActionsCaregiverActivityTable.setIsLoading(true));
        const res = await AxiosAuth.get<
          ApiResponseContainer<PaginatedData<ApiSchemaActivityLogCompany>>
        >(CacheIds.Company, {
          params: {
            PageNumber: pageNumber,
            PageSize: pageSize,
            SearchKey: searchKey,
            CompanyId: companyId,
          },
        });

        if (res.data.isSuccess) {
          api.mutate(res.data.data, false);
        }
        dispatch(ActionsCaregiverActivityTable.setIsLoading(false));
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, pageSize, searchKey, companyId]);

  return api;
}

const AccessObject = {
  Company: CreateAccessObject<
    PaginatedData<ApiSchemaActivityLogCompany>,
    typeof useApiCompanyActivity
  >(CacheIds.Company, useApiCompanyActivity),

  Caregiver: CreateAccessObject<
    PaginatedData<ApiSchemaActivity>,
    typeof useApiCaregiverActivity
  >(CacheIds.Caregiver, useApiCaregiverActivity),

  Patient: CreateAccessObject<
    PaginatedData<ApiSchemaActivity>,
    typeof useApiPatientActivity
  >(CacheIds.Patient, useApiPatientActivity),
};

export const ApiPatientActivity = {
  Patient: AccessObject.Patient,
  Caregiver: AccessObject.Caregiver,
  Company: AccessObject.Company,
};
